/**
 * Trata o erro "Failed to fetch dynamically imported module", que ocorre quando
 * uma nova versão é publicada e os chunks (com hash) referenciados por uma aba
 * já aberta deixam de existir no servidor.
 *
 * Ao detectar a falha, recarrega a página uma vez (o novo index.html aponta para
 * os chunks novos). Um timestamp em sessionStorage evita loop infinito caso o
 * deploy esteja quebrado, mas permite recarregar de novo em deploys futuros.
 */
// ------------------------------- CONSTANTS -------------------------------
const RELOAD_TS_KEY = "app:stale-chunk-reload-ts";
const RELOAD_DEBOUNCE_MS = 10_000;
const FALHAS_ESPECULATIVAS = new WeakSet<object>();

// -------------------------------- HELPERS --------------------------------

function recarregarUmaVez() {
	const agora = Date.now();
	const ultimoReload = Number(sessionStorage.getItem(RELOAD_TS_KEY) ?? 0);

	// Acabou de recarregar -> provável deploy quebrado. Não entra em loop.
	if (agora - ultimoReload < RELOAD_DEBOUNCE_MS) return;

	sessionStorage.setItem(RELOAD_TS_KEY, String(agora));
	globalThis.location.reload();
}

function extrairMensagemErro(motivo: unknown): string {
	if (motivo instanceof Error) return motivo.message;
	if (typeof motivo === "string") return motivo;
	return "";
}

function ehErroDeChunkObsoleto(motivo: unknown): boolean {
	const mensagem = extrairMensagemErro(motivo).toLowerCase();
	return (
		mensagem.includes("failed to fetch dynamically imported module") ||
		mensagem.includes("error loading dynamically imported module") ||
		mensagem.includes("importing a module script failed")
	);
}

function ehFalhaEspeculativa(error: unknown): boolean {
	return typeof error === "object" && error !== null && FALHAS_ESPECULATIVAS.has(error);
}

// ------------------------------- FUNCTIONS -------------------------------
export function classificarFalhaDeImportacao(error: unknown, origem: "prefetch" | "navegacao"): void {
	if (typeof error !== "object" || error === null) return;
	if (origem === "prefetch") FALHAS_ESPECULATIVAS.add(error);
	else FALHAS_ESPECULATIVAS.delete(error);
}

export function setupStaleChunkReload(): () => void {
	const timers = new Set<ReturnType<typeof globalThis.setTimeout>>();

	function avaliarRecarga(error: unknown) {
		// O catch do carregador classifica o erro depois do evento do Vite.
		const timer = globalThis.setTimeout(() => {
			timers.delete(timer);
			if (!ehFalhaEspeculativa(error)) recarregarUmaVez();
		}, 0);
		timers.add(timer);
	}

	function aoFalharPreload(event: Event) {
		// Não prevenir: o Vite precisa rejeitar o import com o erro original.
		avaliarRecarga((event as Event & { payload?: unknown }).payload);
	}

	function aoFalharSemTratamento(event: PromiseRejectionEvent) {
		if (ehErroDeChunkObsoleto(event.reason)) avaliarRecarga(event.reason);
	}

	globalThis.addEventListener("vite:preloadError", aoFalharPreload);
	globalThis.addEventListener("unhandledrejection", aoFalharSemTratamento);
	return () => {
		globalThis.removeEventListener("vite:preloadError", aoFalharPreload);
		globalThis.removeEventListener("unhandledrejection", aoFalharSemTratamento);
		for (const timer of timers) globalThis.clearTimeout(timer);
	};
}
